import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { Box3D } from './types'
import { boxReducer, BoxAction, newBox } from './boxReducer'

export type Mode = 'idle' | 'create' | 'translate' | 'rotate' | 'scale'

interface LabelLayerProps {
  currentFrameId: number
  boxes: Box3D[]
  onTransformStart?: () => void
  onTransformEnd?: () => void
  onBoxAction?: (action: BoxAction) => void
  mode?: 'idle' | 'create' | 'translate' | 'rotate' | 'scale'
  onModeChange?: (mode: 'idle' | 'create' | 'translate' | 'rotate' | 'scale') => void
  selectedId?: string
  onSelectionChange?: (id?: string) => void
}

export const LabelLayer: React.FC<LabelLayerProps> = ({ 
  currentFrameId, 
  boxes, 
  onTransformStart, 
  onTransformEnd,
  onBoxAction,
  mode = 'idle',
  onModeChange,
  selectedId,
  onSelectionChange
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [dragPlane] = useState(() => new THREE.Plane())
  const [dragOffset] = useState(() => new THREE.Vector3())
  
  // Create mode state
  const [createState, setCreateState] = useState<{
    isActive: boolean
    center: THREE.Vector3 | null
    footprint: { lx: number; lz: number }
    ly: number
  }>({
    isActive: false,
    center: null,
    footprint: { lx: 1, lz: 1 },
    ly: 1
  })
  
  const { camera, raycaster, gl } = useThree()
  const selectedRef = useRef<THREE.Group>(null)
  const mouse = useRef(new THREE.Vector2())
  const groundPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0))
  
  // Grid boundaries (adjust these to match your grid size)
  const GRID_BOUNDS = {
    minX: -5, maxX: 5,
    minY: -5, maxY: 5,
    minZ: -5, maxZ: 5
  }
  
  const [localBoxes, setLocalBoxes] = useState<Box3D[]>(boxes)
  
  // Update local boxes when props change
  React.useEffect(() => {
    setLocalBoxes(boxes)
  }, [boxes])
  
  // Filter boxes to current frame
  const frameBoxes = localBoxes.filter(box => box.frameId === currentFrameId)

  // Clamp position within grid boundaries
  const clampPosition = useCallback((position: THREE.Vector3, boxSize: { lx: number, ly: number, lz: number }) => {
    const halfLx = boxSize.lx / 2
    const halfLy = boxSize.ly / 2
    const halfLz = boxSize.lz / 2
    
    return new THREE.Vector3(
      Math.max(GRID_BOUNDS.minX + halfLx, Math.min(GRID_BOUNDS.maxX - halfLx, position.x)),
      Math.max(GRID_BOUNDS.minY + halfLy, Math.min(GRID_BOUNDS.maxY - halfLy, position.y)),
      Math.max(GRID_BOUNDS.minZ + halfLz, Math.min(GRID_BOUNDS.maxZ - halfLz, position.z))
    )
  }, [GRID_BOUNDS.minX, GRID_BOUNDS.maxX, GRID_BOUNDS.minY, GRID_BOUNDS.maxY, GRID_BOUNDS.minZ, GRID_BOUNDS.maxZ])

  // Dispatch reducer action
  const dispatchAction = useCallback((action: BoxAction) => {
    const newBoxes = boxReducer(localBoxes, action)
    setLocalBoxes(newBoxes)
    onBoxAction?.(action)
  }, [localBoxes, onBoxAction])

  // Get mouse position in normalized device coordinates
  const getMousePosition = useCallback((e: any) => {
    const rect = gl.domElement.getBoundingClientRect()
    return {
      x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((e.clientY - rect.top) / rect.height) * 2 + 1
    }
  }, [gl])

  // Raycast to ground plane
  const raycastToGround = useCallback((mousePos: { x: number; y: number }) => {
    const mouseVector = new THREE.Vector2(mousePos.x, mousePos.y)
    raycaster.setFromCamera(mouseVector, camera)
    const intersectionPoint = new THREE.Vector3()
    raycaster.ray.intersectPlane(groundPlane.current, intersectionPoint)
    return intersectionPoint
  }, [raycaster, camera])

  // Handle create mode interactions
  const handleCreateMode = useCallback((e: any) => {
    if (mode !== 'create') return
    
    console.log('Create mode interaction:', { mode, createState })
    
    const mousePos = getMousePosition(e)
    const groundPoint = raycastToGround(mousePos)
    
    console.log('Ground point:', groundPoint)
    
    if (!createState.isActive) {
      // First click: set center
      console.log('Setting center point')
      setCreateState(prev => ({
        ...prev,
        isActive: true,
        center: groundPoint.clone()
      }))
    } else {
      // Drag: update footprint
      if (createState.center) {
        const deltaX = Math.abs(groundPoint.x - createState.center.x)
        const deltaZ = Math.abs(groundPoint.z - createState.center.z)
        
        console.log('Updating footprint:', { deltaX, deltaZ })
        
        setCreateState(prev => ({
          ...prev,
          footprint: {
            lx: Math.max(0.1, deltaX * 2),
            lz: Math.max(0.1, deltaZ * 2)
          }
        }))
      }
    }
  }, [mode, createState, getMousePosition, raycastToGround])

  // Handle mouse wheel for height adjustment
  const handleWheel = useCallback((e: WheelEvent) => {
    if (mode === 'create' && createState.isActive) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      setCreateState(prev => ({
        ...prev,
        ly: Math.max(0.1, prev.ly + delta)
      }))
    }
  }, [mode, createState.isActive])

  // Handle keyboard events
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (mode === 'create' && createState.isActive) {
      if (e.key === 'Enter') {
        // Create the box
        if (createState.center) {
          const newBoxData = newBox({
            frameId: currentFrameId,
            cx: createState.center.x,
            cy: createState.center.y + createState.ly / 2, // Center vertically
            cz: createState.center.z,
            lx: createState.footprint.lx,
            ly: createState.ly,
            lz: createState.footprint.lz,
            yaw: 0,
            cls: 'unknown'
          })
          
          dispatchAction({
            type: 'create',
            box: newBoxData
          })
          
          // Select the new box
          onSelectionChange?.(newBoxData.id)
        }
        
        // Reset create state
        setCreateState({
          isActive: false,
          center: null,
          footprint: { lx: 1, lz: 1 },
          ly: 1
        })
        onModeChange?.('idle')
      } else if (e.key === 'Escape') {
        // Cancel creation
        setCreateState({
          isActive: false,
          center: null,
          footprint: { lx: 1, lz: 1 },
          ly: 1
        })
        onModeChange?.('idle')
      } else if (e.key === 'ArrowUp') {
        // Increase height with arrow keys
        e.preventDefault()
        setCreateState(prev => ({
          ...prev,
          ly: Math.min(5, prev.ly + 0.1)
        }))
      } else if (e.key === 'ArrowDown') {
        // Decrease height with arrow keys
        e.preventDefault()
        setCreateState(prev => ({
          ...prev,
          ly: Math.max(0.1, prev.ly - 0.1)
        }))
      } else if (e.key === 'ArrowLeft') {
        // Decrease width with arrow keys
        e.preventDefault()
        setCreateState(prev => ({
          ...prev,
          footprint: {
            ...prev.footprint,
            lx: Math.max(0.1, prev.footprint.lx - 0.1)
          }
        }))
      } else if (e.key === 'ArrowRight') {
        // Increase width with arrow keys
        e.preventDefault()
        setCreateState(prev => ({
          ...prev,
          footprint: {
            ...prev.footprint,
            lx: Math.min(5, prev.footprint.lx + 0.1)
          }
        }))
      } else if (e.key === 'PageUp') {
        // Increase depth with PageUp/PageDown
        e.preventDefault()
        setCreateState(prev => ({
          ...prev,
          footprint: {
            ...prev.footprint,
            lz: Math.min(5, prev.footprint.lz + 0.1)
          }
        }))
      } else if (e.key === 'PageDown') {
        // Decrease depth with PageUp/PageDown
        e.preventDefault()
        setCreateState(prev => ({
          ...prev,
          footprint: {
            ...prev.footprint,
            lz: Math.max(0.1, prev.footprint.lz - 0.1)
          }
        }))
      }
    } else {
      // Global keyboard shortcuts (when not in create mode)
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault()
        onModeChange?.(mode === 'create' ? 'idle' : 'create')
      } else if (e.key === 'w' || e.key === 'W') {
        e.preventDefault()
        onModeChange?.('translate')
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault()
        onModeChange?.('rotate')
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault()
        onModeChange?.('scale')
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        if (selectedId) {
          dispatchAction({
            type: 'delete',
            id: selectedId
          })
          onSelectionChange?.()
        }
      } else if (e.key === 'l' || e.key === 'L') {
        e.preventDefault()
        if (selectedId) {
          const selectedBox = frameBoxes.find(box => box.id === selectedId)
          if (selectedBox) {
            dispatchAction({
              type: 'update',
              id: selectedId,
              patch: {
                locked: !selectedBox.locked
              }
            })
          }
        }
      }
    }
  }, [mode, createState, currentFrameId, dispatchAction, onModeChange, selectedId, frameBoxes, onSelectionChange])

  // Add event listeners
  useEffect(() => {
    document.addEventListener('wheel', handleWheel, { passive: false })
    document.addEventListener('keydown', handleKeyDown)
    
    return () => {
      document.removeEventListener('wheel', handleWheel)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleWheel, handleKeyDown])

  const handlePointerDown = useCallback((e: any, boxId: string) => {
    if (mode === 'create') {
      handleCreateMode(e)
      return
    }
    
    e.stopPropagation()
    onSelectionChange?.(boxId)
    
    // Start dragging
    setIsDragging(true)
    onTransformStart?.()
    
    // Calculate mouse position in normalized device coordinates
    const mousePos = getMousePosition(e)
    mouse.current.set(mousePos.x, mousePos.y)
    
    // Get the box data to set up drag plane
    const box = frameBoxes.find(b => b.id === boxId)
    if (box) {
      const boxPosition = new THREE.Vector3(box.cx, box.cy, box.cz)
      
      // Set up drag plane at the box's current position
      const cameraDirection = new THREE.Vector3()
      camera.getWorldDirection(cameraDirection)
      dragPlane.setFromNormalAndCoplanarPoint(cameraDirection, boxPosition)
      
      // Calculate intersection point and offset
      const intersectionPoint = new THREE.Vector3()
      raycaster.setFromCamera(mouse.current, camera)
      raycaster.ray.intersectPlane(dragPlane, intersectionPoint)
      
      dragOffset.copy(intersectionPoint).sub(boxPosition)
    }
  }, [mode, handleCreateMode, camera, raycaster, dragPlane, dragOffset, onTransformStart, getMousePosition, onSelectionChange, frameBoxes])

  const handlePointerMove = useCallback((e: any) => {
    if (mode === 'create' && createState.isActive) {
      handleCreateMode(e)
      return
    }
    
    // Only handle dragging if we're actually dragging and mouse button is pressed
    if (!isDragging || !selectedRef.current || e.buttons === 0) return
    
    // Update mouse position
    const mousePos = getMousePosition(e)
    mouse.current.set(mousePos.x, mousePos.y)
    
    // Calculate new position
    const intersectionPoint = new THREE.Vector3()
    raycaster.setFromCamera(mouse.current, camera)
    raycaster.ray.intersectPlane(dragPlane, intersectionPoint)
    
    // Calculate target position
    const targetPosition = intersectionPoint.sub(dragOffset)
    
    // Get box size from the original box data
    const selectedBox = frameBoxes.find(box => box.id === selectedId)
    if (selectedBox) {
      // Clamp position within grid boundaries
      const clampedPosition = clampPosition(targetPosition, {
        lx: selectedBox.lx,
        ly: selectedBox.ly,
        lz: selectedBox.lz
      })
      
      // Update box position
      selectedRef.current.position.copy(clampedPosition)
    }
  }, [mode, createState.isActive, handleCreateMode, isDragging, selectedRef, getMousePosition, raycaster, camera, dragPlane, dragOffset, selectedId, frameBoxes, clampPosition])

  const handlePointerUp = useCallback(() => {
    if (isDragging && selectedRef.current && selectedId) {
      // Get the final position and rotation
      const position = selectedRef.current.position
      const rotation = selectedRef.current.rotation
      
      // Ensure scale is always (1,1,1)
      selectedRef.current.scale.set(1, 1, 1)
      
      // Dispatch update action to reducer
      dispatchAction({
        type: 'update',
        id: selectedId,
        patch: {
          cx: position.x,
          cy: position.y,
          cz: position.z,
          yaw: rotation.y
        }
      })
      
      setIsDragging(false)
      onTransformEnd?.()
    }
  }, [isDragging, selectedRef, selectedId, dispatchAction, onTransformEnd])

  // Update drag plane to be parallel to camera
  useFrame(() => {
    if (selectedRef.current && isDragging) {
      const cameraDirection = new THREE.Vector3()
      camera.getWorldDirection(cameraDirection)
      dragPlane.setFromNormalAndCoplanarPoint(cameraDirection, selectedRef.current.position)
    }
  })

  // Add global event listeners
  React.useEffect(() => {
    const handleGlobalPointerMove = (e: PointerEvent) => {
      handlePointerMove(e)
    }
    
    const handleGlobalPointerUp = () => {
      handlePointerUp()
    }
    
    if (isDragging || (mode === 'create' && createState.isActive)) {
      document.addEventListener('pointermove', handleGlobalPointerMove)
      document.addEventListener('pointerup', handleGlobalPointerUp)
    }
    
    return () => {
      document.removeEventListener('pointermove', handleGlobalPointerMove)
      document.removeEventListener('pointerup', handleGlobalPointerUp)
    }
  }, [isDragging, mode, createState.isActive, handlePointerMove, handlePointerUp])

  return (
    <>
      {/* Background plane for deselection */}
      <mesh 
        position={[0, 0, -10]} 
        onPointerDown={(e) => {
          // Only deselect if not in create mode and not clicking on a box
          if (mode !== 'create' && selectedId) {
            e.stopPropagation()
            onSelectionChange?.(undefined)
          }
        }}
      >
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
      
      {/* Ground plane for create mode */}
      {mode === 'create' && (
        <mesh 
          position={[0, 0, 0]} 
          rotation={[-Math.PI / 2, 0, 0]}
          onPointerDown={(e) => {
            if (mode === 'create') {
              e.stopPropagation()
              handleCreateMode(e)
            }
          }}
        >
          <planeGeometry args={[20, 20]} />
          <meshBasicMaterial 
            transparent 
            opacity={0.1} 
            color="#ffffff"
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      
      {/* Existing boxes */}
      {frameBoxes.map((box) => {
        const isSelected = selectedId === box.id
        const isLocked = box.locked || false
        
        return (
          <group 
            key={box.id}
            ref={isSelected ? selectedRef : undefined}
            position={[box.cx, box.cy, box.cz]} 
            rotation={[0, box.yaw, 0]}
            scale={[1, 1, 1]} // Always maintain scale at (1,1,1)
            userData={{ id: box.id }}
            onPointerDown={isLocked ? undefined : (e) => handlePointerDown(e, box.id)}
          >
            <mesh>
              <boxGeometry args={[box.lx, box.ly, box.lz]} />
              <meshBasicMaterial 
                transparent 
                opacity={isLocked ? 0.1 : (isSelected ? 0.6 : 0.3)}
                color={isLocked ? "#666666" : (isSelected ? "#ff6b35" : "white")}
              />
            </mesh>
          </group>
        )
      })}
      
      {/* Preview box in create mode */}
      {mode === 'create' && createState.isActive && createState.center && (
        <group 
          position={[
            createState.center.x, 
            createState.center.y + createState.ly / 2, 
            createState.center.z
          ]}
          rotation={[0, 0, 0]}
          scale={[1, 1, 1]}
        >
          <mesh>
            <boxGeometry args={[createState.footprint.lx, createState.ly, createState.footprint.lz]} />
            <meshBasicMaterial 
              transparent 
              opacity={0.4} 
              color="#00ff00" 
              wireframe={true}
            />
          </mesh>
        </group>
      )}
    </>
  )
}
